import { Grid } from '@mui/material';
import { memo, useEffect } from 'react';
import { PmiTextField } from 'shell/components/shared/text-field';
import { FormikErrors, FormikTouched } from 'formik';
import { SectionVM } from 'civil-status/view-models/step-form';
import { DatePicker } from '@mui/lab';
import { TEXT_WEDDING_AGREEMENTS } from 'civil-status/constants/text-property-regime';

interface FormNotarialDeedProps {
  disabled?: boolean;
  values?: SectionVM;
  errors?: FormikErrors<SectionVM>;
  touched?: FormikTouched<SectionVM>;
  onChange: (key: keyof SectionVM, value: SectionVM[keyof SectionVM]) => void;
  isMarriage?: boolean;
}

const FormNotarialDeedComponent = ({
  disabled = false,
  values,
  errors,
  touched,
  onChange,
  isMarriage = false,
}: FormNotarialDeedProps): React.ReactElement => {
  useEffect(() => {
    let textContextualAnnotation = '';

    if (values?.datePropertyRegime && values?.datePropertyRegime?.isValid) {
      textContextualAnnotation = `${
        TEXT_WEDDING_AGREEMENTS.Data
      } ${values?.datePropertyRegime.toFormat('dd/MM/yyyy')},`;
    }

    if (values?.repertoireNumber) {
      textContextualAnnotation = `${textContextualAnnotation} ${TEXT_WEDDING_AGREEMENTS.Rep} ${
        values?.repertoireNumber || ''
      }`;
    }

    if (values?.collectionNumber) {
      textContextualAnnotation = `${textContextualAnnotation} ${TEXT_WEDDING_AGREEMENTS.Racc} ${
        values?.collectionNumber || ''
      }`;
    }

    if (values?.repertoireNumber || values?.collectionNumber) {
      textContextualAnnotation = `${textContextualAnnotation},`;
    }

    if (values?.surnameNotary || values?.nameNotary) {
      textContextualAnnotation = `${textContextualAnnotation} ${
        TEXT_WEDDING_AGREEMENTS.NomeCognome
      } ${values?.surnameNotary || ''} ${values?.nameNotary || ''}`;
    }

    if (values?.notarialDistrict) {
      textContextualAnnotation = `${textContextualAnnotation} ${
        TEXT_WEDDING_AGREEMENTS.Distretto
      } ${values?.notarialDistrict || ''}`;
    }

    textContextualAnnotation = `${textContextualAnnotation} ${
      isMarriage
        ? TEXT_WEDDING_AGREEMENTS.ChiusuraTestoMatrimonio
        : TEXT_WEDDING_AGREEMENTS.ChiusuraTestoUnioneCivile
    }`;

    onChange('annotationsPropertyRegime', {
      ...(values?.annotationsPropertyRegime || {}),
      textContextualAnnotation,
    });
  }, [
    values?.surnameNotary,
    values?.nameNotary,
    values?.notarialDistrict,
    values?.datePropertyRegime,
    values?.repertoireNumber,
    values?.collectionNumber,
  ]);

  return (
    <Grid container spacing={2} item>
      <Grid item xs={12} md={6}>
        <PmiTextField
          label="Cognome notaio *"
          aria-label="Cognome notaio"
          id="surnameNotary"
          name="surnameNotary"
          fullWidth
          disabled={disabled}
          helperText={errors?.surnameNotary}
          error={touched?.surnameNotary && Boolean(errors?.surnameNotary)}
          type="text"
          variant="outlined"
          value={values?.surnameNotary || ''}
          onChange={(event) => {
            onChange('surnameNotary', event.target.value);
          }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <PmiTextField
          label="Nome notaio *"
          aria-label="Nome notaio"
          id="nameNotary"
          name="nameNotary"
          fullWidth
          disabled={disabled}
          helperText={errors?.nameNotary}
          error={touched?.nameNotary && Boolean(errors?.nameNotary)}
          type="text"
          variant="outlined"
          value={values?.nameNotary || ''}
          onChange={(event) => {
            onChange('nameNotary', event.target.value);
          }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <PmiTextField
          label="Distretto notarile del notaio *"
          aria-label="Distretto notarile del notaio"
          id="notarialDistrict"
          name="notarialDistrict"
          fullWidth
          disabled={disabled}
          helperText={errors?.notarialDistrict}
          error={touched?.notarialDistrict && Boolean(errors?.notarialDistrict)}
          type="text"
          variant="outlined"
          value={values?.notarialDistrict || ''}
          onChange={(event) => {
            onChange('notarialDistrict', event.target.value);
          }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <DatePicker
          label="Data atto notarile della scelta del regime patrimoniale *"
          aria-label="Data atto notarile della scelta del regime patrimoniale"
          value={values?.datePropertyRegime || null}
          onChange={(date) => {
            onChange('datePropertyRegime', date);
          }}
          disabled={disabled}
          renderInput={(params) => (
            <PmiTextField
              {...params}
              id="datePropertyRegime"
              fullWidth
              helperText={errors?.datePropertyRegime}
              error={touched?.datePropertyRegime && Boolean(errors?.datePropertyRegime)}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <PmiTextField
          label="Repertorio numero"
          aria-label="Repertorio numero"
          id="repertoireNumber"
          name="repertoireNumber"
          fullWidth
          disabled={disabled}
          helperText={errors?.repertoireNumber}
          error={touched?.repertoireNumber && Boolean(errors?.repertoireNumber)}
          type="text"
          variant="outlined"
          value={values?.repertoireNumber || ''}
          onChange={(event) => {
            onChange('repertoireNumber', event.target.value);
          }}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <PmiTextField
          label="Raccolta numero"
          aria-label="Raccolta numero"
          id="collectionNumber"
          name="collectionNumber"
          fullWidth
          disabled={disabled}
          helperText={errors?.collectionNumber}
          error={touched?.collectionNumber && Boolean(errors?.collectionNumber)}
          type="text"
          variant="outlined"
          value={values?.collectionNumber || ''}
          onChange={(event) => {
            onChange('collectionNumber', event.target.value);
          }}
        />
      </Grid>
    </Grid>
  );
};

FormNotarialDeedComponent.displayName = 'Form Notarial Deed';

const FormNotarialDeed = memo(FormNotarialDeedComponent);

export default FormNotarialDeed;
